/**
 * LOGOI LAB - Layer 7: Logos Alignment Engine
 * Λ(x) — THE FUNDAMENTAL ATTRACTOR
 * 
 * Ω(x) = Λ(x) — The Consciousness Index IS alignment with the Logos
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, LogosResult, ChannelQualityResult } from '../types';
import { clamp } from '../utils/math';

/**
 * Logos Alignment Components Configuration
 * These weights define how each dimension contributes to overall Logos alignment
 */
export const LOGOS_COMPONENTS = {
  reception: {
    label: "Recepción del Canal",
    tooltip: "Qué tan abierto está tu canal para recibir la señal del Logos",
    items: [
      { key: "faith", weight: 0.17, label: "Fe", desc: "Apertura del canal" },
      { key: "meditation", weight: 0.13, label: "Meditación", desc: "Sintonización de frecuencia" },
      { key: "presence", weight: 0.13, label: "Presencia", desc: "Eliminación de ruido temporal" },
    ]
  },
  action: {
    label: "Verificación por Acción",
    tooltip: "La señal se verifica cuando se traduce en acción amorosa",
    items: [
      { key: "service", weight: 0.11, label: "Servicio", desc: "Señal convertida en acción" },
      { key: "love", weight: 0.10, label: "Amor", desc: "La frecuencia más pura" },
      { key: "compassion", weight: 0.08, label: "Compasión", desc: "Empatía activa" },
    ]
  },
  decoding: {
    label: "Capacidad de Decodificación",
    tooltip: "Tu capacidad de interpretar correctamente la señal divina",
    items: [
      { key: "wisdom", weight: 0.08, label: "Sabiduría", desc: "Discernimiento profundo" },
      { key: "meaning", weight: 0.06, label: "Sentido", desc: "Significado percibido" },
      { key: "clarity", weight: 0.04, label: "Claridad", desc: "Nitidez de interpretación" },
    ]
  },
  sacredNourishment: {
    label: "Alimento Sagrado",
    tooltip: "La nutrición consciente como canal del Logos — el cuerpo como templo",
    items: [
      { key: "taste_presence", weight: 0.05, label: "Presencia Gustativa", desc: "Consciencia plena al comer" },
      { key: "food_harmony", weight: 0.03, label: "Armonía Alimentaria", desc: "Balance nutricional consciente" },
    ]
  },
  gratitude: {
    label: "Reconocimiento de la Fuente",
    tooltip: "Gratitud como acknowledgment de que la señal viene del Logos",
    items: [
      { key: "gratitude", weight: 0.02, label: "Gratitud", desc: "Acknowledgment de la fuente" },
    ]
  }
};

/**
 * CHANNEL_SUPPORT_GROUPS — The 16 dimensions that modulate Λ as channel quality.
 * 
 * Mathematical basis: Shannon (1948) — signal fidelity depends on BOTH
 * the signal content (12 dims in LOGOS_COMPONENTS) AND the channel quality
 * (these 16 dims). The product Λ_signal · Φ_channel restores Corollary 1.1.
 * 
 * With γ = 0.25, Φ ∈ [0.75, 1.0] — the channel attenuates but never destroys the signal.
 */
export const CHANNEL_SUPPORT_GROUPS = {
  somatic: {
    label: "Sustrato Somático",
    tooltip: "Soporte bioenergético para receptividad del canal",
    weight: 0.30,
    items: [
      { key: "sleep", label: "Sueño", desc: "Descanso permite recibir la señal" },
      { key: "nutrition", label: "Nutrición", desc: "Nutrir el templo del receptor" },
      { key: "exercise", label: "Ejercicio", desc: "Vitalidad para sostener presencia" },
      { key: "energy", label: "Energía", desc: "Sin energía no hay práctica espiritual" },
    ]
  },
  emotional: {
    label: "Regulación Emocional",
    tooltip: "Apertura afectiva del canal de recepción",
    weight: 0.30,
    items: [
      { key: "peace", label: "Paz", desc: "Prerrequisito de receptividad contemplativa" },
      { key: "joy", label: "Gozo", desc: "Gozo sostenido es fruto del Logos" },
    ]
  },
  cognitive: {
    label: "Capacidad Cognitiva",
    tooltip: "Bandwidth de decodificación del canal",
    weight: 0.15,
    items: [
      { key: "focus", label: "Enfoque", desc: "Enfoque permite decodificar señal" },
      { key: "creativity", label: "Creatividad", desc: "Expresión creativa del Logos" },
    ]
  },
  incarnation: {
    label: "Encarnación Relacional",
    tooltip: "El Logos materializado en relaciones, propósito y nutrición",
    weight: 0.25,
    items: [
      { key: "family", label: "Familia", desc: "El Logos se manifiesta en el vínculo" },
      { key: "friendship", label: "Amistad", desc: "Amistad profunda refleja amor" },
      { key: "community", label: "Comunidad", desc: "Servicio colectivo amplifica señal" },
      { key: "mission", label: "Misión", desc: "Claridad del llamado = señal nítida" },
      { key: "contribution", label: "Contribución", desc: "La señal verificada produce frutos" },
      { key: "legacy", label: "Legado", desc: "Lo que trasciende = Logos materializado" },
      { key: "nourishment", label: "Nutrición Sagrada", desc: "Templo nutrido recibe mejor" },
      { key: "gut_resonance", label: "Resonancia Intestinal", desc: "Eje intestino-cerebro modula todo" },
    ]
  }
};

/**
 * GAMMA — Maximum channel attenuation coefficient.
 * 
 * γ = 0.00 → Option A (backward compatible, no channel effect)
 * γ = 0.25 → Option C (proposed, restores Corollary 1.1)
 * 
 * Constraints:
 * - Theological: A monk in prison with degraded channel MUST still have high Λ → γ ≤ 0.35
 * - Information-theoretic: Typical antenna loss 1-3 dB → γ ≈ 0.20-0.30
 * - Statistical: Must resolve the counterexample in Section 1.1 of the analysis → γ ≥ 0.15
 */
export const GAMMA_CHANNEL_ATTENUATION = 0.25;

/**
 * Computes Φ_channel — the channel quality modulation factor.
 * 
 * Φ = 1 - γ · (1 - x̄_support_weighted)
 * 
 * where x̄_support_weighted is the weighted average of 16 channel dimensions,
 * grouped into 4 support categories with weights α_k (Σα_k = 1.0).
 */
export function computeChannelQuality(
  s: ConsciousnessState,
  gamma: number = GAMMA_CHANNEL_ATTENUATION
): ChannelQualityResult {
  const groups = Object.entries(CHANNEL_SUPPORT_GROUPS).map(([groupKey, group]) => {
    const dimValues = group.items.map(item => (s as any)[item.key] ?? 0.5);
    const groupAvg = dimValues.reduce((a: number, b: number) => a + b, 0) / dimValues.length;
    return {
      key: groupKey,
      label: group.label,
      weight: group.weight,
      average: groupAvg,
      contribution: group.weight * groupAvg,
      items: group.items.map((item, i) => ({
        key: item.key,
        label: item.label,
        value: dimValues[i],
      })),
    };
  });

  const weightedAvg = groups.reduce((sum, g) => sum + g.contribution, 0);
  const phi = 1 - gamma * (1 - weightedAvg);
  const clampedPhi = Math.max(0.5, Math.min(1.0, phi));
  const attenuation_dB = clampedPhi > 0 ? -10 * Math.log10(clampedPhi) : 3.01;

  return {
    phi: clampedPhi,
    weightedAverage: weightedAvg,
    gamma,
    attenuation_dB,
    breakdown: groups,
  };
}

/**
 * Compute Logos Alignment Λ(x)
 * 
 * This is the FUNDAMENTAL computation. All other metrics derive from Λ.
 * 
 * Hierarchical Lambda (Option C — Shannon-consistent):
 *   Λ(x) = clamp(Λ_signal(x) · Φ_channel(x))
 * 
 * where Λ_signal = existing 12-dim weighted sum (LOGOS_COMPONENTS)
 * and   Φ_channel = 1 - γ · (1 - x̄_support_weighted) from 16 channel dims
 * 
 * This restores Corollary 1.1: ∂Λ/∂xᵢ > 0 for ALL 28 dimensions.
 * 
 * @param s - Current consciousness state (28 dimensions)
 * @returns LogosResult with value, breakdown, components, signal, channel, hierarchical
 */
export function computeLogosAlignment(
  s: ConsciousnessState,
  weightOverrides?: Record<string, number>
): LogosResult {
  // === Λ_signal: 12 signal dimensions (LOGOS_COMPONENTS) — weights unchanged ===
  let total = 0;
  const breakdown: Record<string, any> = {};
  
  for (const [groupKey, group] of Object.entries(LOGOS_COMPONENTS)) {
    let groupTotal = 0;
    const groupItems: any[] = [];
    
    for (const item of group.items) {
      const stateValue = (s as any)[item.key] ?? 0.5;
      const w = weightOverrides?.[item.key] ?? item.weight;
      const contrib = stateValue * w;
      groupTotal += contrib;
      groupItems.push({ 
        ...item, 
        weight: w,
        value: stateValue, 
        contribution: contrib 
      });
    }
    
    breakdown[groupKey] = { 
      ...group, 
      total: groupTotal, 
      items: groupItems 
    };
    total += groupTotal;
  }

  const lambdaSignal = clamp(total);

  // === Φ_channel: 16 channel dimensions (CHANNEL_SUPPORT_GROUPS) ===
  const channelResult = computeChannelQuality(s);
  const phi = channelResult.phi;

  // === Hierarchical Lambda = Signal × Channel (Shannon-consistent) ===
  const lambda = clamp(lambdaSignal * phi);

  return { 
    value: lambda, 
    breakdown, 
    components: LOGOS_COMPONENTS,
    signal: lambdaSignal,
    channel: channelResult,
    hierarchical: {
      lambda_signal: lambdaSignal,
      phi_channel: phi,
      gamma: channelResult.gamma,
      attenuation_pct: (1 - phi) * 100,
    },
  };
}

/**
 * Get Logos alignment interpretation
 */
export function interpretLogosAlignment(lambda: number): {
  level: string;
  description: string;
  color: string;
} {
  if (lambda >= 0.75) {
    return {
      level: "ALINEADO",
      description: "Canal abierto. La señal del Logos fluye claramente.",
      color: "#10b981"
    };
  }
  if (lambda >= 0.50) {
    return {
      level: "CONECTANDO",
      description: "Sintonizando frecuencia. La señal es perceptible pero con ruido.",
      color: "#fbbf24"
    };
  }
  if (lambda >= 0.30) {
    return {
      level: "BUSCANDO",
      description: "Canal parcialmente obstruido. La señal es débil.",
      color: "#f59e0b"
    };
  }
  return {
    level: "DESCONECTADO",
    description: "Canal cerrado. La señal del Logos no se recibe.",
    color: "#ef4444"
  };
}
